import {
  createContext,
  ReactElement,
  useContext,
  useEffect,
  useState,
} from 'react';
import { getActivePreset, presetOptions as allPresetOptions } from './presets';
import type { PresetOption, TimeSpan } from './types';
import { limitRange } from './utils';

interface DateRangeContextProps {
  activePreset?: PresetOption;
  presetOptions: PresetOption[];
  iconButton?: boolean;
  initialValue: string[];
  value: string[];
  setValue: (range: string[]) => void;
  max: number;
  onChange: (range?: string[]) => void;
  onClear: () => void;
  timeSpan?: TimeSpan;
}

const DateRangeContext = createContext({} as DateRangeContextProps);

interface DateRangeProviderProps {
  children: ReactElement;
  iconButton?: boolean;
  initialValue?: string[];
  max?: number;
  onChange: (range?: string[]) => void;
  timeSpan?: TimeSpan;
}

interface TimeSpanConfig {
  min: number;
  max: number;
}

const timeSpanConfigs: Record<TimeSpan, TimeSpanConfig> = {
  medium: {
    min: 0,
    max: 2_678_400, // 31 days
  },
  long: {
    min: 90_000, // 25 hours
    max: Infinity,
  },
};
const fallbackTimeSpanConfig = { min: 0, max: Infinity };

const filterPresetOptions = (max: number, timeSpan?: TimeSpan) => {
  const config = timeSpan ? timeSpanConfigs[timeSpan] : fallbackTimeSpanConfig;

  return allPresetOptions.filter(
    (option) =>
      option.seconds <= max &&
      option.seconds <= config.max &&
      option.seconds >= config.min
  );
};

const DateRangeProvider = ({
  children,
  iconButton,
  initialValue = ['', ''],
  max = Infinity,
  onChange,
  timeSpan,
}: DateRangeProviderProps) => {
  const [from, to] = limitRange(initialValue, initialValue, max);
  const [value, setValue] = useState([from, to]);

  const activePreset = getActivePreset(value);
  const presetOptions = filterPresetOptions(max, timeSpan);

  const handleChange = (range?: string[]) => {
    const arr = range || value;
    onChange(arr[0] || arr[1] ? arr : undefined);
  };

  const handleClear = () => {
    onChange(undefined);
  };

  const handleSetValue = (range: string[]) => {
    const withinLimit = limitRange(value, range, max);
    setValue(withinLimit);
  };

  useEffect(
    () => {
      if (from !== value[0] || to !== value[1]) {
        setValue([from || '', to || '']);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [from, to]
  );

  // Ensure initial range is within max limit
  useEffect(
    () => {
      if (from !== initialValue[0] || to !== initialValue[1]) {
        handleChange([from, to]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [from, to]
  );

  return (
    <DateRangeContext.Provider
      value={{
        activePreset,
        presetOptions,
        initialValue,
        iconButton,
        value,
        setValue: handleSetValue,
        max,
        onChange: handleChange,
        onClear: handleClear,
        timeSpan,
      }}
    >
      {children}
    </DateRangeContext.Provider>
  );
};

export const useDateRange = () => useContext(DateRangeContext);

export default DateRangeProvider;
